import {Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {UserService} from '../../../../services/user-services/user.service';
import {Location, NgClass, NgIf, NgOptimizedImage} from '@angular/common';
import {DashboardComponent} from './dashboard/dashboard.component';
import {finalize, take} from 'rxjs/operators';
import {PostReservationState} from '../../../../models/postReservationState';
import {IconComponent} from '../../../../shared/components/icons-svgs/icons-svgs.component';
import {NgCircleProgressModule} from 'ng-circle-progress';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {User} from '../../../../models/user';
import {TemplateContactComponent} from './template/template-contact/template-contact.component';
import {Partner} from '../../../../models/partner';
import {TemplateStepComponent} from './template/template-step/template-step.component';
import {TemplateEndComponent} from './template/template-end/template-end.component';
import {PlatformService} from "../../../../services/platform-service.service";
import {AlertService} from "../../../../services/alert.service";
import {TemplateErrorComponent} from "./template/template-error/template-error.component";

/**
 * @TODO : - Faut voir comment gérer la fin du parcours selon l'évolution de celui-ci
 *         - Clean ce qui doit l'être selon les choix faits (page de fin de parcours : oui/non, page de fin de parcours affichée dans le process, etc...)
 *
 * @CommentBy : Gwen, 12.04.2024
 */


@Component({
  selector: 'app-post-reservation-layout',
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    DashboardComponent,
    NgOptimizedImage,
    IconComponent,
    NgCircleProgressModule,
    TranslateModule,
    TemplateContactComponent,
    TemplateStepComponent,
    TemplateEndComponent,
    TemplateErrorComponent,
  ],
  templateUrl: './post-reservation-layout.component.html',
  styleUrl: './post-reservation-layout.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PostReservationLayoutComponent implements OnInit, OnDestroy {

  // langue de l'utilisateur
  public locale: string = 'en';
  // Récupère l'utilisateur connecté
  public user: User;
  // étape à laquelle se trouve l'utilisateur
  public currentUserStep: number = null;
  // étape visitée par l'utilisateur
  public openedStep: PostReservationState;
  // tableau de toutes les étapes du partenaire
  public postReservationStateArray: PostReservationState[] = [];
  // étape principale sur laquelle l'utilisateur a cliqué
  public openedUserStep: PostReservationState = null;
  // détecte le chargement des datas du partenaire - change uniquement dans OnInit
  public isLoading: boolean = false;
  // Détecte le chargement des appels API
  public isUpdating: boolean = false;
  // pourcentage de complétion de l'utilisateur
  public headerPourcent: number = 0;
  // couleur de la barre de progression selon le pourcentage de complétion
  public progressColor: string = '#FF4747';
  // statut de l'étape sur laquelle a cliqué l'utilisateur
  public stepValidation: 'validated' | 'current' | 'next' = 'next';
  // ID du booking
  private bookingId: number = 0;
  // récupère les appels API pour unsubscribe quand on détruit le composant
  private subscriber$ = [];
  // Tous les ids des steps pour calculer le pourcentage et set les étapes validées /!\ NE PAS SORT
  public postReservationStatesIds: number[] = [];
  // Informations du partenaire
  public partner: Partner = null;
  // Détermine s'il y a une page annexe à afficher
  public showPage: 'contact' | 'validation' | 'error' | null = null;
  // True si l'utilisateur a fini le process
  public hasFinishProcess: boolean = false;
  public contactFormUrl: string;


  constructor(
    private userService: UserService,
    private router: Router,
    private modaleService: NgbModal,
    private translateService: TranslateService,
    private location: Location,
    private platformService: PlatformService,
    private alertService: AlertService,
  ) {
  }

  ngOnInit(): void {
    if(this.platformService.isBrowserPlatform()){
      this.locale = this.translateService.currentLang;
      this.setContactFormUrl();
      history.pushState(null, '');
      const currentLocationState: any = this.location.getState();
      this.user = this.userService.userInformations;
      this.bookingId = JSON.parse(localStorage.getItem('tempBooking')).booking_id;
      if (!this.bookingId && localStorage.getItem('tempRedirect')) {
        this.bookingId = parseInt(localStorage.getItem('tempRedirect'));
      }
      this.subscriber$.push(this.translateService.onLangChange.subscribe(lang => {
        this.locale = lang.lang;
      }));
      if(!this.bookingId){
        if(this.translateService.currentLang == 'fr'){
          this.alertService.openSnackBar('Tu n\'as pas accepté les cookies et malheureusement cette partie du site n\'est accessible qu\'avec les cookies activées', 'snack-error');
        }
        else {
          this.alertService.openSnackBar('You did not accept cookies and unfortunately this part of the site is only accessible with cookies enabled.', 'snack-error');
        }
        this.showPage = 'error';
      }
      else {
        if (this.bookingId) {
          this.isLoading = true;
          this.subscriber$.push(
            this.userService.getPostReservationsSteps(this.bookingId)
              .pipe(take(1), finalize(() => {
                this.isLoading = false;
              }))
              .subscribe(data => {
                  // Récupère les infos du partenaire
                  this.partner = data.partnersInformation;
                  delete data.partnersInformation;
                  // Récupère toutes les étapes du partenaire & check le type du step
                  this.postReservationStateArray = this.setStepOrder(data);
                  // Récupère l'étape où en est l'utilisateur
                  this.currentUserStep = data.currentUserStep ? data.currentUserStep : this.postReservationStateArray[0]?.id;
                  // Vérifie si l'utilisateur a fini le parcours ou pas
                  if (this.currentUserStep === 255) {
                    this.hasFinishProcess = true;
                  }
                },
                error => {
                  if (error === 'This booking is not validated') {
                    this.router.navigate([this.locale+'/accommodation/pending']);
                  }
                })
          );
        }
        // Browser btn click
        this.subscriber$.push(this.location.subscribe(e => {
          // Si on est sur une étape/sous étape/page annexes & qu'on clique sur retour, on renvoie sur le dashboard post booking
          if ((this.openedUserStep || this.showPage) && !currentLocationState) {
            this.openedUserStep = null;
            this.showPage = null;
          }
        }));
      }
    }
    else {
      if(this.translateService.currentLang == 'fr'){
        this.alertService.openSnackBar('Ton navigateur n\'est pas compatible avec cette page, contactes-nous pour plus d\'infos', 'snack-error');
      }
      else {
        this.alertService.openSnackBar('Your browser is not compatible with this page. Contact us for more information.', 'snack-error');
      }
      this.showPage = 'contact';
    }

  }

  ngOnDestroy(): void {
    this.subscriber$.forEach(s$ => {
      s$.unsubscribe();
    });
  }

  public changeTemplate(e: PostReservationState, needCheck?: boolean): void {
    if(this.platformService.isBrowserPlatform()){
      e['main_order'] = this.postReservationStateArray.indexOf(e) + 1;
      // La sous étape éventuelle à afficher
      this.openedStep = e;

      // Si on clique depuis le dashboard, on veut vérifier des choses
      if (!needCheck) {
        // L'étape principale à afficher
        this.openedUserStep = e;
        // S'il y a des sous étapes, on veut savoir si tout a été validé
        if (this.openedUserStep && this.openedUserStep.childs && this.openedUserStep.childs.length > 0) {
          // On récupère la dernière sous étape
          const lastChild = this.openedUserStep.childs[this.openedUserStep.childs.length - 1];
          // On la compare avec le currentStep
          let i: 'validated' | 'current' | 'next';
          if (!this.hasFinishProcess) {
            i = this.compareIndexes(this.currentUserStep, lastChild.id);
          } else {
            i = 'validated';
          }

          // Si ce n'est pas validé ou que c'est celle en cours, on affiche l'étape à valider
          if (i !== 'validated') {
            const lastValidatedStep = this.getLastValidatedStepFromMainStep();
            if (lastValidatedStep) {
              this.openedStep = lastValidatedStep;
            }
          }
        }
      }
      if (!this.hasFinishProcess) {
        this.stepValidation = this.compareIndexes(this.currentUserStep, this.openedStep.id);
      } else {
        this.stepValidation = 'validated';
      }
    }


  }

  public onWantOtherStep(step: PostReservationState): void {
    if(this.platformService.isBrowserPlatform()){
      const place = this.compareIndexes(this.openedStep.id, step.id);
      let validated: 'validated' | 'current' | 'next';
      if (!this.hasFinishProcess) {
        validated = this.compareIndexes(this.currentUserStep, step.id);
      } else {
        validated = 'validated';
      }

      if (validated !== 'next') {
        this.changeTemplate(step, true);
      } else {
        if (place === 'next') {
          this.nextStep();
        }
      }
    }

  }

  public nextStep(): void {
    if(this.platformService.isBrowserPlatform()){
      this.getNextStep(false);
    }
  }

  public openContactPage(): void {
    // this.showContactPage = true;
    this.showPage = 'contact';
  }

  public goToProfile(): void {
    this.router.navigate(['/account/my-profile'], {queryParams: {tab: 'purchase'}});
  }

  public confirmNextStepClick(finish?): void {
    if(this.platformService.isBrowserPlatform()){
      let nextStepId: number = 0;
      if (!finish) {
        nextStepId = this.openedUserStep.next_step_id;
        if (this.openedUserStep.type !== 'single') {
          const child: PostReservationState = this.openedUserStep.childs.find(c => c.id === this.openedStep.id);
          if (child) {
            nextStepId = child.next_step_id;
          }
        }
      }

      this.isUpdating = true;
      this.subscriber$.push(
        this.userService.updatePostReservationsSteps(this.bookingId, (finish ? 255 : nextStepId)).pipe(take(1), finalize(() => {
          this.modaleService.dismissAll();
          this.isUpdating = false;
          if (!finish) {
            this.getNextStep(true);
          }
        })).subscribe()
      );
    }
  }

  /**
   * @TODO : revoir quand il y aura le starter pack
   */
  public finishProcess(): void {
    if(this.platformService.isBrowserPlatform()){
      if (!this.hasFinishProcess) {
        this.currentUserStep = 255;
        this.confirmNextStepClick(true);
      }
      this.hasFinishProcess = true;
    }
  }

  /**
   * Compare secondStep à firstStep.
   * Si secondStep est après firstStep, on renvoie 'next' ; s'il est avant, on renvoie 'validated'
   * @param firstStep
   * @param secondStep
   * @private
   */
  private compareIndexes(firstStep: number | PostReservationState, secondStep: number | PostReservationState): 'validated' | 'next' | 'current' {
    if(this.platformService.isBrowserPlatform()){
      let index1;
      let index2;
      if (firstStep instanceof PostReservationState && secondStep instanceof PostReservationState) {
        index1 = this.postReservationStateArray.indexOf(firstStep);
        index2 = this.postReservationStateArray.indexOf(secondStep);
      } else if (typeof firstStep === 'number' && typeof secondStep === 'number') {
        index1 = this.postReservationStatesIds.indexOf(firstStep);
        index2 = this.postReservationStatesIds.indexOf(secondStep);
      }
      if (index1 < index2) {
        return 'next';
      }
      if (index1 > index2) {
        return 'validated';
      }
      return 'current';
    }

  }

  /**
   * On récupère les ids des différentes étapes et sous étapes
   * On s'assure que les childs sont bien un array et non un JSON
   * Modifier le type au besoin
   * @param state
   * @private
   */
  private checkStepType(state): string {
    this.postReservationStatesIds.push(state.id);
    if (state.type !== 'single' && state.childs && state.childs.length !== 0) {
      if (!Array.isArray(state.childs)) {
        let tempChilds = [];
        Object.keys(state.childs).forEach(key => {
          tempChilds.push(state.childs[key]);
        });
        state.childs = tempChilds;
      }
      state.childs.forEach(s => {
        this.postReservationStatesIds.push(s.id);
      });
    }
    return state.type;
  }

  /**
   * Sort les steps dans le bon ordre & check le type
   * @param data
   * @private
   */
  private setStepOrder(data): PostReservationState[] {
    let tempArray = [];
    Object.keys(data).forEach(d => {
      // @TODO : && data[d].order !== 100 -> à supprimer dès qu'on a le starter pack
      if (data[d] && data[d].id && data[d].order !== 100) {
        data[d].type = this.checkStepType(data[d]);
        tempArray.push(data[d]);
      }
    });
    tempArray = tempArray.sort((a, b) => a.order - b.order);
    return tempArray;
  }

  private getNextStep(isUpdate: boolean = false): void {
    // On cherche le prochain step parmi les grandes étapes
    let nextState: PostReservationState = this.postReservationStateArray.find(state => state.id === this.openedStep.next_step_id);
    // L'étape voulue est dans les grandes étapes
    if (nextState) {
      this.openedUserStep = nextState;
      this.openedStep = nextState;
    }
    // L'étape voulue n'est pas dans les grandes étapes
    else {
      if (this.openedStep.type !== 'single') {
        nextState = this.openedUserStep.childs.find(c => c.id === this.openedStep.next_step_id);
      }
    }
    // Si c'est une étape nouvellement validée, on met à jour le pourcentage de complétion
    if (isUpdate) {
      this.currentUserStep = nextState.id;
    }

    this.openedStep = nextState;
    this.openedUserStep['main_order'] = this.postReservationStateArray.indexOf(this.openedUserStep) + 1;
    if (!this.hasFinishProcess) {
      this.stepValidation = this.compareIndexes(this.currentUserStep, this.openedStep.id);
    } else {
      this.stepValidation = 'validated';
    }

  }

  /**
   * Récupère le dernier child validé par l'utilisateur pour l'étape choisie/ouverte
   * @private
   */
  private getLastValidatedStepFromMainStep(): PostReservationState {
    const index1: number = this.postReservationStatesIds.indexOf(this.currentUserStep);
    const lastChild: PostReservationState = this.openedUserStep.childs[this.openedUserStep.childs.length - 1];
    let index2: number = this.postReservationStatesIds.indexOf(lastChild.id);
    // Toutes les étapes ne sont pas validées par l'utilisateur, il faut trouver la bonne
    if (index2 > index1) {
      let step: PostReservationState;
      this.openedUserStep.childs.forEach(c => {
        let i = this.postReservationStatesIds.indexOf(c.id);
        if (i === index1) {
          step = c;
        }
      });
      return step;
    }
    // Toutes les étapes sont validées par l'utilisateur, on l'envoie sur la dernière
    return lastChild;
  }

  private setContactFormUrl(): void {
    this.contactFormUrl = 'https://form.typeform.com/to/Skse0ywG';
    if (this.translateService.currentLang === 'fr') {
      this.contactFormUrl = 'https://form.typeform.com/to/vfP7UpuV';
    }
  }
}
